/**
 * Talking to the online season server. Works in the browser and in Node (bot clients, soak and security tests):
 * everything takes a Supabase client signed in as one player.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Command, CommandResult, GameState } from "@bd/sim";

export interface Season {
  id: string;
  name: string;
  status: "lobby" | "playing" | "resolving" | "finished";
  quarter: number;
  week: number;
  deadline: string | null;
  quarter_hours: number;
  staging: boolean;
  created_by: string;
}
export interface Member {
  season_id: string;
  user_id: string;
  seat: number;
  player_id: string;
  company_name: string;
  last_seen: string;
}

export async function gunzipBase64(b64: string): Promise<string> {
  const bin = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return await new Response(new Blob([bin]).stream().pipeThrough(new DecompressionStream("gzip"))).text();
}

export async function gzipBase64(text: string): Promise<string> {
  const buf = new Uint8Array(await new Response(new Blob([text]).stream().pipeThrough(new CompressionStream("gzip"))).arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

const must = <T>(r: { data: T; error: { message: string } | null }): T => {
  if (r.error) throw new Error(r.error.message);
  return r.data;
};

/** After signing in: joins the allow-list's players. Returns your display name, or null if this account isn't invited. */
export async function claimPlayer(sb: SupabaseClient): Promise<string | null> {
  return must(await sb.rpc("claim_player")) as string | null;
}

export async function createSeason(sb: SupabaseClient, name: string, company: string, opts: { staging?: boolean; hours?: number } = {}): Promise<string> {
  return must(await sb.rpc("create_season", { p_name: name, p_company: company, p_staging: opts.staging ?? false, p_hours: opts.hours ?? 12 })) as string;
}

export async function joinSeason(sb: SupabaseClient, seasonId: string, company: string): Promise<number> {
  return must(await sb.rpc("join_season", { p_season: seasonId, p_company: company })) as number;
}

export async function mySeasons(sb: SupabaseClient): Promise<Season[]> {
  return must(await sb.from("seasons").select("*").order("created_at", { ascending: false })) as Season[];
}

export async function seasonMembers(sb: SupabaseClient, seasonId: string): Promise<Member[]> {
  return must(await sb.from("season_members").select("*").eq("season_id", seasonId).order("seat")) as Member[];
}

export async function getSeason(sb: SupabaseClient, seasonId: string): Promise<Season> {
  return must(await sb.from("seasons").select("*").eq("id", seasonId).single()) as Season;
}

export async function readyList(sb: SupabaseClient, seasonId: string): Promise<{ seat: number; ready: boolean }[]> {
  return must(await sb.rpc("ready_list", { p_season: seasonId })) as { seat: number; ready: boolean }[];
}

/** Your company and what you can see of everyone else's, as of the start of the current quarter. */
export async function myView(sb: SupabaseClient, seasonId: string): Promise<{ quarter: number; week: number; state: GameState } | null> {
  const row = must(await sb.from("player_views").select("quarter, week, data").eq("season_id", seasonId).maybeSingle()) as { quarter: number; week: number; data: string } | null;
  if (!row) return null;
  return { quarter: row.quarter, week: row.week, state: JSON.parse(await gunzipBase64(row.data)) as GameState };
}

export async function submitQuarter(sb: SupabaseClient, seasonId: string, quarter: number, commands: Command[], ready: boolean): Promise<void> {
  must(await sb.rpc("submit_quarter", { p_season: seasonId, p_quarter: quarter, p_commands: commands, p_ready: ready }));
}

export async function myResults(sb: SupabaseClient, seasonId: string, quarter: number): Promise<CommandResult[] | null> {
  const row = must(await sb.from("quarter_results").select("results").eq("season_id", seasonId).eq("quarter", quarter).maybeSingle()) as { results: CommandResult[] } | null;
  return row?.results ?? null;
}

export type ServerReply = { status?: string; error?: string; quarter?: number; week?: number; hash?: string; ms?: number; simMs?: number };

/** Asks the server to start the season (its creator) or settle the quarter (settles only once everyone is ready or time is up). */
export async function callServer(sb: SupabaseClient, action: "start" | "resolve", seasonId: string, force = false): Promise<ServerReply> {
  const { data, error } = await sb.functions.invoke("resolve-quarter", { body: { action, season_id: seasonId, force } });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const body = ctx && typeof ctx.json === "function" ? await ctx.json().catch(() => null) : null;
    return { error: body?.error ?? error.message };
  }
  return data as ServerReply;
}
